/**
 * DeclineToSign — the always-available "Decline to sign" control + confirmation (CB-5.7, FR-52).
 *
 * UX-DR20: decline is FIRST-CLASS and one tap away from every ceremony step — never buried, never
 * styled as a suppressed/secondary action, no dark patterns. Tapping it opens a confirmation that
 * captures an OPTIONAL reason (empty allowed), then calls the token-scoped decline endpoint (halts
 * the envelope + notifies the sender server-side). On success the parent shows the terminal declined
 * end-state. Keyboard- and AT-operable; ≥44px target; the dialog traps initial focus and the result
 * is announced.
 */

import { useState } from 'react';
import { XCircle } from 'lucide-react';
import { contractService } from '@/services/contractService';
import { accentTextOnLight } from '@/lib/tenantAccent';
import { useDialogA11y } from './useDialogA11y';

interface DeclineToSignProps {
  token: string;
  accent: string;
  /** Called after a successful decline so the shell can render the declined end-state. */
  onDeclined: () => void;
}

export default function DeclineToSign({ token, accent, onDeclined }: DeclineToSignProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // a11y: Escape closes, Tab is trapped within the dialog, focus returns to the trigger on close.
  const dialogRef = useDialogA11y<HTMLDivElement>(open, () => {
    if (!submitting) {
      setOpen(false);
      setError(null);
    }
  });

  async function confirmDecline() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await contractService.declineToSign(token, reason.trim() || undefined);
    if (result.success) {
      onDeclined();
      return;
    }
    setError(result.error || "We couldn't record your decline. Please try again.");
    setSubmitting(false);
  }

  return (
    <>
      {/* First-class, persistent decline control (UX-DR20). Plain wording, no legalese. */}
      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-medium text-slate-600 underline underline-offset-2 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          Decline to sign
        </button>
      </div>

      {open ? (
        <div
          ref={dialogRef}
          tabIndex={-1}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center focus:outline-none"
          role="dialog"
          aria-modal="true"
          aria-labelledby="decline-title"
        >
          <div className="w-full max-w-[480px] rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-3 flex items-center gap-2">
              <XCircle className="h-6 w-6 text-red-600" aria-hidden="true" />
              <h2 id="decline-title" className="text-lg font-semibold text-slate-900">
                Decline to sign?
              </h2>
            </div>
            <p className="text-base leading-relaxed text-slate-600">
              If you decline, this contract will be halted and the sender will be notified. You can
              add a reason if you'd like (optional).
            </p>
            <label htmlFor="decline-reason" className="mt-4 block text-sm font-medium text-slate-700">
              Reason (optional)
            </label>
            <textarea
              id="decline-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              placeholder="Let the sender know why (optional)"
            />
            {error ? (
              <p className="mt-2 text-sm text-red-600" role="alert">
                {error}
              </p>
            ) : null}
            <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
              <button
                type="button"
                onClick={confirmDecline}
                disabled={submitting}
                className="flex min-h-[44px] flex-1 items-center justify-center rounded-xl bg-red-600 px-4 text-base font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {submitting ? 'Declining…' : 'Decline to sign'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setError(null);
                }}
                disabled={submitting}
                className="flex min-h-[44px] flex-1 items-center justify-center rounded-xl border border-slate-300 px-4 text-base font-semibold text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ color: accentTextOnLight(accent) }}
              >
                Keep signing
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
